(function () {
  'use strict';

  const API_BASE = '/apps/tecdoc';
  const STORAGE_KEY = 'vehicle_fitment_vehicle';

  const $ = (root, selector) => root ? root.querySelector(selector) : null;
  const $$ = (root, selector) => root ? Array.from(root.querySelectorAll(selector)) : [];

  function esc(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function savedVehicle(storeId, tecDocProfile) {
    try {
      const vehicle = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!vehicle) return null;
      if (vehicle.storeId !== storeId) return null;
      const savedProfile = vehicle.tecDocProfile || vehicle.profile;
      if (savedProfile !== tecDocProfile) return null;
      return vehicle;
    } catch (_) {
      return null;
    }
  }

  function vehicleId(vehicle) {
    return vehicle && (vehicle.vehicleId || vehicle.VehicleId || vehicle.id || vehicle.Id);
  }

  function translateVehicleText(value) {
    return window.ArcherVehicleLabels && window.ArcherVehicleLabels.translateText
      ? window.ArcherVehicleLabels.translateText(value)
      : String(value || '');
  }

  function vehicleName(vehicle) {
    return [
      vehicle && (vehicle.makeName || vehicle.MakeName),
      translateVehicleText(vehicle && (vehicle.modelName || vehicle.ModelName)),
      translateVehicleText(vehicle && (vehicle.typeName || vehicle.TypeName))
    ].filter(Boolean).join(' ');
  }

  function vehicleMetaRows(root, vehicle) {
    return [
      [root.dataset.engineLabel || 'Engine', vehicle && (vehicle.engineCode || vehicle.EngineCode)],
      [root.dataset.powerLabel || 'Power', vehicle && [
        vehicle.powerKw || vehicle.PowerKw,
        vehicle.powerHp || vehicle.PowerHp
      ].filter(Boolean).join(' / ')],
      [root.dataset.yearLabel || 'Year', vehicle && [
        vehicle.yearFrom || vehicle.YearFrom,
        vehicle.yearTo || vehicle.YearTo
      ].filter(Boolean).join(' - ')]
    ].filter((row) => row[1]);
  }

  function vehicleImage(vehicle) {
    return vehicle && (vehicle.imageUrl || vehicle.ImageUrl || '');
  }

  function money(value, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(Number(value || 0));
    } catch (_) {
      return `${Number(value || 0).toFixed(2)} ${currency || 'GBP'}`;
    }
  }

  function displaySku(sku) {
    return String(sku || '')
      .trim()
      .replace(/-\d+(?:-\d+)?(?:_\d+)?$/i, '')
      .replace(/_\d+$/i, '');
  }

  function productUrl(product) {
    const raw = String(product.url || product.productUrl || product.ProductUrl || '#').trim();
    if (!raw || raw === '#') return '#';
    try {
      const url = new URL(raw, window.location.origin);
      if (/^\/(?:products|collections|pages|search)(?:\/|\?|$)/.test(url.pathname)) {
        return `${url.pathname}${url.search}${url.hash}`;
      }
      return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : raw;
    } catch (_) {
      return raw;
    }
  }

  function resolveTecDocProfile(root, value) {
    const fallback = root.dataset.tecDocProfile || 'uk';
    const requested = String(value || fallback).trim() || fallback;
    return fallback === 'uk' && requested === 'en' ? 'uk' : requested;
  }

  function finderUrl(root) {
    const target = root.dataset.finderUrl || '/';
    const url = new URL(target, window.location.origin);
    url.searchParams.set('changeVehicle', '1');
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function setState(root, title, text) {
    const state = $(root, '[data-results-state]');
    if (!state) return;
    state.hidden = false;
    state.innerHTML = `<h2>${esc(title)}</h2><p>${esc(text)}</p>`;
  }

  function clearState(root) {
    const state = $(root, '[data-results-state]');
    if (!state) return;
    state.hidden = true;
    state.innerHTML = '';
  }

  function renderSummary(root, data, fallbackVehicle) {
    const summary = $(root, '[data-results-summary]');
    if (!summary) return;

    const vehicle = data && data.vehicle ? data.vehicle : fallbackVehicle;
    if (!vehicle) {
      summary.innerHTML = '';
      return;
    }

    const total = data ? Number(data.total || 0) : 0;
    const image = vehicleImage(vehicle);
    const label = vehicleName(vehicle);
    const make = vehicle && (vehicle.makeName || vehicle.MakeName || '');
    const metaRows = vehicleMetaRows(root, vehicle);
    const trustItems = [root.dataset.trustOne, root.dataset.trustTwo, root.dataset.trustThree].filter(Boolean);

    summary.innerHTML = `
      <div class="vehicle-results__vehicle-media">
        ${image ? `<img src="${esc(image)}" alt="${esc(label)}" loading="lazy">` : `<span>${esc(String(make || label).slice(0, 1))}</span>`}
      </div>
      <div class="vehicle-results__selected">
        <span class="vehicle-results__summary-label">${esc(root.dataset.summaryLabel || 'Selected vehicle')}</span>
        <strong>${esc(label)}</strong>
        ${data ? `<em>${esc(total)} compatible product${total === 1 ? '' : 's'} found</em>` : '<em>Preparing compatible products...</em>'}
        ${metaRows.length ? `<dl>${metaRows.map((row) => `<div><dt>${esc(row[0])}</dt><dd>${esc(row[1])}</dd></div>`).join('')}</dl>` : ''}
        ${trustItems.length ? `<ul>${trustItems.map((item) => `<li>${esc(item)}</li>`).join('')}</ul>` : ''}
      </div>
      <a class="vehicle-results__change" href="${esc(finderUrl(root))}">${esc(root.dataset.changeLabel || 'Change vehicle')}</a>`;
  }

  function renderFamilies(root, data) {
    const familiesNode = $(root, '[data-results-families]');
    if (!familiesNode) return;

    const families = data && Array.isArray(data.families) ? data.families : [];
    if (families.length <= 1) {
      familiesNode.innerHTML = '';
      familiesNode.hidden = true;
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const selected = params.get('family') || '';
    familiesNode.hidden = false;
    familiesNode.innerHTML = [
      `<a href="${esc(buildPageUrl('', 1))}" ${selected ? '' : 'aria-current="true"'}>${esc(root.dataset.allFamiliesLabel || 'All matching parts')}</a>`,
      ...families.map((family) => {
        const slug = family.family || family.Family || '';
        const name = family.familyName || family.FamilyName || slug;
        const total = family.total || family.Total || 0;
        return `<a href="${esc(buildPageUrl(slug, 1))}" ${slug === selected ? 'aria-current="true"' : ''}>${esc(name)} <span>${esc(total)}</span></a>`;
      })
    ].join('');
  }

  function buildPageUrl(family, page) {
    const url = new URL(window.location.pathname, window.location.origin);
    const current = new URLSearchParams(window.location.search);
    ['vehicleId', 'storeId', 'tecDocProfile'].forEach((key) => {
      if (current.get(key)) url.searchParams.set(key, current.get(key));
    });
    if (family) url.searchParams.set('family', family);
    if (page > 1) url.searchParams.set('page', String(page));
    return `${url.pathname}${url.search}`;
  }

  function renderGrid(root, data) {
    const grid = $(root, '[data-results-grid]');
    if (!grid) return;

    const products = data && Array.isArray(data.products) ? data.products : [];
    grid.innerHTML = '';

    if (!products.length) {
      setState(root, root.dataset.emptyProductsTitle || 'No compatible products found', root.dataset.emptyProductsText || 'No published products are currently listed for this vehicle.');
      return;
    }

    clearState(root);
    products.forEach((product) => {
      const title = product.title || product.Title || '';
      const vendor = product.vendor || product.Vendor || '';
      const sku = product.sku || product.Sku || '';
      const cleanSku = displaySku(sku);
      const image = product.imageUrl || product.ImageUrl || '';
      const price = product.price || product.Price || 0;
      const cardTrust = root.dataset.cardTrust || 'Tested | 1 year warranty | Fast shipping';
      const item = document.createElement('li');
      item.className = 'vehicle-results__item';
      item.innerHTML = `
        <a class="vehicle-results__card" href="${esc(productUrl(product))}">
          <span class="vehicle-results__media">
            ${image ? `<img src="${esc(image)}" alt="${esc(title)}" loading="lazy">` : '<span>No image</span>'}
          </span>
          <span class="vehicle-results__copy">
            <span class="vehicle-results__meta-row">
              ${vendor ? `<em>${esc(vendor)}</em>` : '<em>Refurbished</em>'}
              <i>Compatible</i>
            </span>
            <strong>${esc(title)}</strong>
            ${cleanSku ? `<small>${esc(cleanSku)}</small>` : ''}
            ${cardTrust ? `<span class="vehicle-results__card-trust">${esc(cardTrust)}</span>` : ''}
            <span class="vehicle-results__price-row">
              <b>${esc(money(price, root.dataset.currencyCode || 'GBP'))}</b>
              <span>View part</span>
            </span>
          </span>
        </a>`;
      grid.appendChild(item);
    });
  }

  function renderPagination(root, data) {
    const nav = $(root, '[data-results-pagination]');
    if (!nav) return;

    const totalPages = Number(data && data.totalPages || 1);
    const current = Number(data && data.page || 1);
    if (totalPages <= 1) {
      nav.innerHTML = '';
      nav.hidden = true;
      return;
    }

    nav.hidden = false;
    nav.innerHTML = Array.from({ length: totalPages }, (_, index) => {
      const page = index + 1;
      return `<a href="${esc(buildPageUrl(new URLSearchParams(window.location.search).get('family') || '', page))}" ${page === current ? 'aria-current="page"' : ''}>${page}</a>`;
    }).join('');
  }

  async function load(root, requestId) {
    const params = new URLSearchParams(window.location.search);
    const storeId = params.get('storeId') || root.dataset.storeId || 'archer-diesel';
    const tecDocProfile = resolveTecDocProfile(root, params.get('tecDocProfile'));
    const stored = savedVehicle(storeId, tecDocProfile);
    const requestedVehicleId = params.get('vehicleId') || vehicleId(stored);

    if (!requestedVehicleId) {
      renderSummary(root, null, null);
      setState(root, root.dataset.emptyTitle || 'Select your vehicle first', root.dataset.emptyText || 'Choose your make, model and engine to see compatible products.');
      return;
    }

    renderSummary(root, null, stored);
    setState(root, root.dataset.loadingTitle || 'Loading compatible products', root.dataset.loadingText || 'Checking available products for your selected vehicle.');

    const query = new URLSearchParams({
      vehicleId: String(requestedVehicleId),
      storeId,
      tecDocProfile,
      family: params.get('family') || root.dataset.family || 'injectors',
      page: params.get('page') || '1',
      limit: root.dataset.limit || '48'
    });

    const response = await fetch(`${API_BASE}/vehicle-results?${query.toString()}`, { headers: { Accept: 'application/json' } });
    const json = await response.json().catch(() => null);
    if (!response.ok || !json || json.success === false) {
      throw new Error((json && json.error) || `Request failed (${response.status})`);
    }
    if (root.dataset.resultsRequestId !== requestId) return;

    const data = json.data || {};
    if (data.vehicle) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          ...data.vehicle,
          vehicleId: data.vehicleId || requestedVehicleId,
          storeId: data.storeId || query.get('storeId'),
          tecDocProfile: query.get('tecDocProfile'),
          family: data.family || query.get('family'),
          savedAt: Date.now()
        }));
      } catch (_) { }
    }

    renderSummary(root, data, stored);
    renderFamilies(root, data);
    renderGrid(root, data);
    renderPagination(root, data);
  }

  function init(root) {
    if (root.dataset.resultsReady === 'true') return;
    root.dataset.resultsReady = 'true';
    const requestId = String((Number(root.dataset.resultsRequestId || 0) || 0) + 1);
    root.dataset.resultsRequestId = requestId;
    load(root, requestId).catch((error) => {
      if (root.dataset.resultsRequestId !== requestId) return;
      setState(root, root.dataset.errorTitle || 'Results could not be loaded', error.message || root.dataset.errorText || 'Please try again.');
    });
  }

  function boot() {
    $$(document, '[data-vehicle-results]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('vehiclefitment:selected', (event) => {
    const root = $(document, '[data-vehicle-results]');
    const vehicle = event.detail || {};
    const id = vehicleId(vehicle);
    if (!root || !id) return;

    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('vehicleId', String(id));
    url.searchParams.set('storeId', vehicle.storeId || root.dataset.storeId || 'archer-diesel');
    url.searchParams.set('tecDocProfile', resolveTecDocProfile(root, vehicle.tecDocProfile));
    url.searchParams.set('family', vehicle.family || root.dataset.family || 'injectors');
    window.location.href = `${url.pathname}${url.search}`;
  });
  document.addEventListener('vehiclefitment:cleared', () => {
    const root = $(document, '[data-vehicle-results]');
    if (!root) return;

    window.history.replaceState({}, document.title, window.location.pathname);
    root.dataset.resultsReady = 'false';
    const grid = $(root, '[data-results-grid]');
    const families = $(root, '[data-results-families]');
    const pagination = $(root, '[data-results-pagination]');
    if (grid) grid.innerHTML = '';
    if (families) {
      families.innerHTML = '';
      families.hidden = true;
    }
    if (pagination) {
      pagination.innerHTML = '';
      pagination.hidden = true;
    }
    init(root);
  });
  document.addEventListener('shopify:section:load', boot);
})();